// Copyright (C) 2023 Cochise Ruhulessin
// 
// All rights reserved. No warranty, explicit or implicit, provided. In
// no event shall the author(s) be liable for any claim or damages.
// 
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
import axios, { AxiosInstance } from 'axios'
import CanonicalException from '../types'


interface IExpectedProduct {
  productId: string
  sku: string
  displayName: string
  quantity: number
  grade: string
  received: number
}


interface ITransactionDevice {
  battery: number
  document: string
  grade: string
  imei: string[]
  name: string
  registered: Date
  registrar: string
  expected: boolean
  sku: string | null
  product_id: string | null
}


class Transaction {
  private documentRef: string
  private data: any
  private service: TransactionService

  constructor(service: TransactionService, data: any) {
    this.service = service
    this.data = data
    this.documentRef = data.documentRef
  }

  getDocumentRef(): string {
    return this.documentRef
  }

  getDisplayName() {
    return `${this.documentRef} van ${this.data.party.name}`
  }

  getProducts(): IExpectedProduct[] {
    return this.data.expected
  }

  getReceivedProducts(): ITransactionDevice[] {
    return this.data.received.map((device: any) => {
      return {
        ...device,
        registered: new Date(Date.parse(device.registered))
      }
    })
  }

  getTotalExpected(): number {
    return this.data.totalExpected
  }

  getTotalReceived(): number {
    return this.data.totalReceived
  }

  isClosed(): boolean {
    return this.data.status === "closed"
  }

  async close(): Promise<void> {
    this.data.status = "closed"
    return await this.service.close(this.documentRef)
  }

  async register(imei: string): Promise<{device: ITransactionDevice, expected: IExpectedProduct[]}> {
    return await this.service.register(this.documentRef, imei)
  }
}


class TransactionService {
  http: AxiosInstance

  constructor() {
    this.http = axios.create({
      baseURL: "/api/transactions",
      withCredentials: true
    })
  }

  async close(documentRef: string): Promise<void> {
    await this.http.post(`${documentRef}/close`)
  }

  async get(documentRef: string): Promise<Transaction | null> {
    try {
      const response = await this.http.get(`/${documentRef}`)
      return new Transaction(this, response.data)
    } catch (e: any) {
      if (!e.isAxiosError || e.response.status !== 404) { throw e}
      return null
    }
  }

  async register(documentRef: string, imei: string): Promise<{device: ITransactionDevice, expected: IExpectedProduct[]}> {
    try {
      const response = await this.http.post(
        `/${documentRef}/register`,
        {imei: imei}
      )
      return {
        device: {
          ...response.data.device,
          registered: new Date(Date.parse(response.data.device.registered))
        },
        expected: response.data.expected
      }
    } catch(e: any) {
      if (e.response.status === 403) { throw new CanonicalException("DEVICE_RECEIVED")}
      throw e
    }
  }
}


export {
  Transaction,
  TransactionService
}

export type { IExpectedProduct, ITransactionDevice }